import { useEffect, useState } from 'react';
import { api, type VendorDetail } from '../api';
import { ts, useDebounced, useLoad } from '../lib';
import { VendorRecordSections } from './VendorRecord';

// Suppliers, their assigned camps, and catalog coverage. Open one to change where its purchase orders
// go (go/no-go #23) and the account number each camp's POs to it carry (#12).
export function VendorsPanel({ canEdit }: { canEdit: boolean }) {
  const [q, setQ] = useState('');
  const dq = useDebounced(q);
  const [open, setOpen] = useState<number | null>(null);
  const { data, err } = useLoad(() => api.commerceVendors(dq), [dq]);

  if (open != null) return <VendorDetailPane id={open} canEdit={canEdit} onBack={() => setOpen(null)} />;

  return (
    <div className="stack">
      <h1>Vendors</h1>
      <p className="sub">Suppliers, their assigned camps, and catalog coverage — open one to edit where its POs go and its full record</p>
      <div className="toolbar">
        <input className="search" placeholder="Search vendors…" value={q} onChange={(e) => setQ(e.target.value)} />
      </div>
      {err && <p className="msg bad">⚠ {err}</p>}
      <section className="card">
        <table>
          <thead><tr><th>Vendor</th><th>Channel</th><th>Account</th><th className="num">Camps</th><th className="num">Active offers</th></tr></thead>
          <tbody>
            {(data ?? []).map((v) => (
              <tr key={v.id} className="row-click" onClick={() => setOpen(v.id)}>
                <td style={{ fontWeight: 600 }}>{v.name}</td>
                <td>{v.is_edi ? <span className="pill edi">EDI</span> : <span className="pill">email</span>}</td>
                <td className="mono">{v.account_id ?? '—'}</td>
                <td className="num">{v.camps}</td>
                <td className="num">{Number(v.offers).toLocaleString()}</td>
              </tr>
            ))}
            {data && data.length === 0 && <tr><td colSpan={5} className="loading">No vendors match.</td></tr>}
          </tbody>
        </table>
        {!data && !err && <p className="loading">Loading…</p>}
      </section>
    </div>
  );
}

const list = (a: string[]) => (a.length ? a.join(', ') : '—');

function VendorDetailPane({ id, canEdit, onBack }: { id: number; canEdit: boolean; onBack: () => void }) {
  const [d, setD] = useState<VendorDetail | null>(null);
  const [form, setForm] = useState({ salesRepEmail: '', ccEmails: '', bccEmails: '' });
  const [acct, setAcct] = useState<Record<number, string>>({});
  const [busy, setBusy] = useState('');
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');

  const load = () => api.commerceVendor(id).then((r) => {
    setD(r);
    setForm({ salesRepEmail: r.vendor.sales_rep_email ?? '', ccEmails: r.vendor.cc_emails ?? '', bccEmails: r.vendor.bcc_emails ?? '' });
    setAcct(Object.fromEntries(r.camps.map((c) => [c.org_id, c.account_number ?? ''])));
  }).catch((e) => setErr((e as Error).message));
  useEffect(() => { load(); }, [id]);

  async function saveContact() {
    setBusy('contact'); setMsg(''); setErr('');
    try {
      const r = await api.commerceVendorContact(id, form);
      setMsg(r.changed.length ? `Saved — the next PO goes to ${list(r.recipients.to)}.` : 'Nothing changed.');
      load();
    } catch (e) { setErr((e as Error).message); } finally { setBusy(''); }
  }

  async function saveAccount(orgId: number, camp: string) {
    setBusy(`acct-${orgId}`); setMsg(''); setErr('');
    try {
      const r = await api.commerceVendorAccount(id, orgId, acct[orgId] ?? '');
      setMsg(r.accountNumber ? `${camp}: POs now carry account ${r.accountNumber}.` : `${camp}: account number cleared.`);
      load();
    } catch (e) { setErr((e as Error).message); } finally { setBusy(''); }
  }

  if (!d) return <div className="stack"><div><button className="btn-sm ghost" onClick={onBack}>← Back to vendors</button></div>{err ? <p className="msg bad">⚠ {err}</p> : <p className="loading">Loading…</p>}</div>;
  const v = d.vendor;
  const dirty = form.salesRepEmail !== (v.sales_rep_email ?? '') || form.ccEmails !== (v.cc_emails ?? '') || form.bccEmails !== (v.bcc_emails ?? '');

  return (
    <div className="stack">
      <div><button className="btn-sm ghost" onClick={onBack}>← Back to vendors</button></div>
      <h1>{v.name ?? `Vendor ${v.id}`}</h1>
      <p className="sub">Vendor {v.id}{v.account_id ? ` · ${v.account_id}` : ''}{v.is_edi ? ' · EDI' : ''}{v.is_active === false ? ' · inactive' : ''}{v.updated_at ? ` · updated ${ts(v.updated_at)}` : ''}</p>
      {(msg || err) && <p className={`msg ${err ? 'bad' : 'good'}`}>{err ? `⚠ ${err}` : msg}</p>}

      <section className="card vendor-contact">
        <h2 style={{ marginTop: 0 }}>Where its purchase orders go</h2>
        <div className="form-row">
          <label className="field vendor-field"><span>Ordering email (To) — required</span>
            <input className="search wide" value={form.salesRepEmail} onChange={(e) => setForm({ ...form, salesRepEmail: e.target.value })} placeholder="orders@vendor.com, rep@vendor.com" />
          </label>
          <label className="field vendor-field"><span>CC</span>
            <input className="search wide" value={form.ccEmails} onChange={(e) => setForm({ ...form, ccEmails: e.target.value })} placeholder="—" />
          </label>
          <label className="field vendor-field"><span>BCC</span>
            <input className="search wide" value={form.bccEmails} onChange={(e) => setForm({ ...form, bccEmails: e.target.value })} placeholder="—" />
          </label>
        </div>
        <div className="edi-actions">
          <span className="muted">
            The next PO goes to <strong>{list(d.recipients.to)}</strong>{d.recipients.usesAccountEmail ? ' (the account email — no ordering email is on file)' : ''}
            {d.recipients.cc.length > 0 && <> · cc {list(d.recipients.cc)}</>}{d.recipients.bcc.length > 0 && <> · bcc {list(d.recipients.bcc)}</>}.
            Separate addresses with commas. Account email (login, not edited here): {v.email ?? '—'}.
          </span>
          <button className="btn-sm" disabled={!dirty || busy !== ''} onClick={saveContact}>{busy === 'contact' ? 'Saving…' : 'Save'}</button>
        </div>
        <p className="muted vendor-note">Until the cutover, Magento still sends the live POs: make the same change there too. The data refresh from Magento keeps what you save here.</p>
      </section>

      <section className="card">
        <h2 style={{ marginTop: 0 }}>Camps and their account numbers</h2>
        <p className="muted" style={{ marginTop: 0 }}>Each camp's POs to {v.name ?? 'this vendor'} carry its account number — the first field of the PO file and a line of the emailed PO. Blank clears it. Until the cutover, change it in Magento too; the data refresh keeps what you save here.</p>
        <table>
          <thead><tr><th>Camp</th><th>Assignment</th><th>Account number</th><th></th></tr></thead>
          <tbody>
            {d.camps.map((c) => (
              <tr key={c.org_id}>
                <td>{c.camp} <span className="mono">#{c.org_id}</span></td>
                <td>{c.is_active ? <span className="pill type">active</span> : <span className="muted">inactive</span>}</td>
                <td><input className="search short" aria-label={`Account number for ${c.camp}`} value={acct[c.org_id] ?? ''} onChange={(e) => setAcct({ ...acct, [c.org_id]: e.target.value })} placeholder="—" /></td>
                <td>
                  <button className="btn-sm" disabled={(acct[c.org_id] ?? '') === (c.account_number ?? '') || busy !== ''} onClick={() => saveAccount(c.org_id, c.camp)}>
                    {busy === `acct-${c.org_id}` ? 'Saving…' : 'Save'}
                  </button>
                </td>
              </tr>
            ))}
            {d.camps.length === 0 && <tr><td colSpan={4} className="loading">No active camp is assigned this vendor.</td></tr>}
          </tbody>
        </table>
      </section>

      <VendorRecordSections id={id} canEdit={canEdit} />
    </div>
  );
}
